import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';

const POLL_INTERVAL = 30000; // 30 seconds

export function useStatsData(initialHours = 24) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hours, setHours] = useState(initialHours);
  const requestIdRef = useRef(0);
  const intervalRef = useRef(null);
  const fetchingRef = useRef(false);

  const fetchStats = useCallback(async () => {
    if (fetchingRef.current) return; // skip if previous fetch still in flight
    fetchingRef.current = true;
    const id = ++requestIdRef.current;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const response = await fetch(`${API_BASE_URL}/api/stats?hours=${hours}`, {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (requestIdRef.current !== id) return;
      if (result.success) {
        setData(result);
        setError(null);
      } else {
        setError(result.error || 'Failed to load stats');
      }
    } catch (err) {
      if (err.name === 'AbortError') return; // 15s timeout — not a user-visible error
      if (requestIdRef.current !== id) return;
      setError(err.message);
    } finally {
      if (requestIdRef.current === id) {
        fetchingRef.current = false;
        setLoading(false);
      }
    }
  }, [hours]);

  useEffect(() => {
    setLoading(true);
    fetchStats();

    intervalRef.current = setInterval(fetchStats, POLL_INTERVAL);

    return () => {
      ++requestIdRef.current;
      fetchingRef.current = false; // allow immediate fetch after hours change
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchStats]);

  return { data, loading, error, hours, setHours };
}
